import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { invitationService } from "@/services/employees";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/store/authStore";
import { RegisterForm } from "@/components/auth/RegisterForm";

interface InvitationData {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  store_id: string;
  expires_at: string;
  accepted: boolean;
  token: string;
  stores?: { name: string };
}

export default function AcceptInvitation() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [status, setStatus] = useState<
    | "loading"
    | "success"
    | "error"
    | "register_required"
    | "login_required"
    | "expired"
  >("loading");
  const [message, setMessage] = useState(
    t("staffInvitation.verifyingInvitation")
  );
  const [invitationData, setInvitationData] = useState<InvitationData | null>(
    null
  );

  useEffect(() => {
    const inviteToken = searchParams.get("token");

    if (!inviteToken) {
      setStatus("error");
      setMessage(t("staffInvitation.invalidInvitationLink"));
      return;
    }

    const verifyInvitation = async () => {
      try {
        // Check if invitation exists and is valid
        const { data: invitation, error } = await supabase
          .from("employee_invitations")
          .select(
            `
            *,
            stores!inner(name)
          `
          )
          .eq("token", inviteToken)
          .eq("accepted", false)
          .maybeSingle();

        if (error || !invitation) {
          setStatus("error");
          setMessage(t("staffInvitation.invalidOrExpiredInvitation"));
          return;
        }

        setInvitationData(invitation);

        // Check if invitation has expired
        if (new Date(invitation.expires_at) < new Date()) {
          setStatus("expired");
          setMessage(t("staffInvitation.invitationHasExpired"));
          return;
        }

        // Check if user exists with this email
        const { data: existingUser } = await supabase
          .from("users")
          .select("id")
          .eq("email", invitation.email)
          .maybeSingle();

        if (existingUser) {
          // User exists - redirect to login with invitation token
          const loginUrl = `/login?token=${inviteToken}&redirect=/dashboard/staff`;
          navigate(loginUrl);
        } else {
          // New user - show registration form
          setStatus("register_required");
          setMessage(
            t("staffInvitation.completeRegistrationToAcceptInvitation")
          );
        }
      } catch (err) {
        console.error("Error during invitation verification:", err);
        setStatus("error");
        setMessage(t("staffInvitation.errorVerifyingInvitation"));
      }
    };

    verifyInvitation();
  }, [searchParams, t, navigate]);

  // Process the invitation when user is authenticated
  const processInvitation = async (inviteToken: string) => {
    setStatus("loading");
    setMessage(t("staffInvitation.processingInvitation"));

    try {
      console.log("Processing invitation with token:", inviteToken);
      const success = await invitationService.acceptInvitation(inviteToken);

      if (success) {
        console.log("✅ Invitation accepted successfully");
        setStatus("success");
        setMessage(t("staffInvitation.invitationAccepted"));
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard/staff");
        }, 2000);
      } else {
        console.error("❌ Failed to accept invitation");
        setStatus("error");
        setMessage(t("staffInvitation.invitationFailed"));
      }
    } catch (error) {
      console.error("❌ Error accepting invitation:", error);
      setStatus("error");
      setMessage(t("staffInvitation.invitationError"));
    }
  };

  // Handle user authentication completion
  const handleAuthComplete = () => {
    if (invitationData?.token) {
      console.log("Authentication completed, processing invitation");
      // Delay to ensure auth is fully processed
      setTimeout(() => {
        processInvitation(invitationData.token);
      }, 1000);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-bold">{t("staffInvitation.title")}</h1>
          {invitationData?.stores?.name && (
            <p className="text-sm text-gray-600">
              {t("staffInvitation.invitationFrom")}{" "}
              <strong>{invitationData.stores.name}</strong>
            </p>
          )}

          {status === "loading" && (
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p>{message}</p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <div className="rounded-full bg-green-100 p-3 inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-green-600">{message}</p>
              <p className="text-sm text-gray-500">
                {t("staffInvitation.redirectingToDashboard")}
              </p>
            </div>
          )}

          {status === "expired" && (
            <div className="space-y-4">
              <div className="rounded-full bg-yellow-100 p-3 inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-yellow-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <p className="text-yellow-600">{message}</p>
              <p className="text-sm text-gray-600">
                {t("staffInvitation.contactStoreNewInvitation", {
                  storeName: invitationData?.stores?.name || t("theStore"),
                })}
              </p>
            </div>
          )}

          {status === "login_required" && (
            <div className="space-y-4">
              <div className="rounded-full bg-blue-100 p-3 inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <p className="text-blue-600">{message}</p>
              <p className="text-sm text-gray-600">
                {t("staffInvitation.existingAccountEmail")}{" "}
                <strong>{invitationData?.email}</strong>
              </p>
              <Button onClick={() => navigate("/login")} className="w-full">
                {t("goToLogin")}
              </Button>
            </div>
          )}

          {status === "register_required" && (
            <div className="space-y-4">
              <p className="text-gray-600">{message}</p>
              <div className="border-t my-6"></div>
              <RegisterForm
                onAuthComplete={handleAuthComplete}
                isStaffInvite={true}
                defaultEmail={invitationData?.email}
              />
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <div className="rounded-full bg-red-100 p-3 inline-block">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <p className="text-red-600">{message}</p>
              <Button onClick={() => navigate("/login")}>
                {t("goToLogin")}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
